import React, { useState } from "react";
import {
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    TableCell,
    TableRow,
    Tooltip,
} from "@mui/material";
import styled from "styled-components";

import { Draggable, DraggableProvided } from "react-beautiful-dnd";

import { ObserveState } from "../../CommonUI/StateManagement/ObserveState";
import VariablePicker from "../../CommonUI/VariablePicker/VariablePicker";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { InlineTextEdit } from "../../CommonUI/InlineEditor/InlineTextEdit";
import { ExpressionConfiguration } from "../../rule-engine-core/Expressions/ExpressionConfiguration";

import { FormConfiguration } from "../FormConfiguration";
import { FormRule, FormRuleType } from "../Structure/FormRule";

import { AttentionIcon } from "../../CommonUI/Icon/AttentionIcon";
import { StyledCheckBox } from "../../CommonUI/BuildlingBlocks/StyledCheckbox";
import { UISwitch } from "../../CommonUI/BuildlingBlocks/UISwitch";
import { ExpressionView } from "../../Expressions/ExpressionView";
import { DeleteIcon } from "../../CommonUI/Icon/DeleteIcon";
import { StyledFormControlLabel } from "../../CommonUI/BuildlingBlocks/StyledFormControlLabel";
import { StyledSelect } from "../../CommonUI/BuildlingBlocks/StyledSelect";
import { UIRadioGroup } from "../../CommonUI/BuildlingBlocks/UIRadioGroup";
import { StyledButton } from "../../CommonUI/BuildlingBlocks/StyledButton";
import { CloseGrayIcon } from "../../CommonUI/Icon/CloseGrayIcon";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export const XFormRuleRow = ({
    form,
    rule,
    index,
    onAction,
}: {
    form: FormConfiguration;
    rule: FormRule;
    index: number;
    onAction: (element: HTMLButtonElement) => void;
}) => {
    return (
        <ObserveState
            listenTo={rule}
            control={() => {
                let designIssues = rule.getDesignValidationIssues(form);
                let expressionIssues = rule.expression.expressionRoot.getValidationIssues();
                return (
                    <Draggable
                        draggableId={"ruleRowDrag" + index}
                        index={index}
                    >
                        {(provided: DraggableProvided) => {
                            return (
                                <StyledTableRow
                                    ref={(node: HTMLTableRowElement) => {
                                        provided.innerRef(node);
                                    }}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                >
                                    <TableCell>
                                        <CellRender>
                                            <StyledSelect
                                                value={rule.ruleType}
                                                fullWidth
                                                onChange={(event) => {
                                                    rule.ruleType = event.target.value as FormRuleType;
                                                    StateManager.propertiesChanged(rule, ["ruleType", "data"]);
                                                }}
                                                data-testid="XFormRule-ruleType"
                                            >
                                                <MenuItem value={FormRuleType.CHANGE_FOCUS}>
                                                    {FormRuleType.CHANGE_FOCUS}
                                                </MenuItem>
                                                <MenuItem value={FormRuleType.CHANGE_VALUE}>
                                                    {FormRuleType.CHANGE_VALUE}
                                                </MenuItem>
                                                <MenuItem value={FormRuleType.CONTROL_ENABLED}>
                                                    {FormRuleType.CONTROL_ENABLED}
                                                </MenuItem>
                                                <MenuItem value={FormRuleType.CONTROL_VISIBILITY}>
                                                    {FormRuleType.CONTROL_VISIBILITY}
                                                </MenuItem>
                                                <MenuItem value={FormRuleType.EXIT_FORM}>
                                                    {FormRuleType.EXIT_FORM}
                                                </MenuItem>
                                                <MenuItem value={FormRuleType.VALIDATION}>
                                                    {FormRuleType.VALIDATION}
                                                </MenuItem>
                                            </StyledSelect>

                                            <WatchValuesRender data-testid="XFormRule-watchingValues">
                                                <CheckboxListLayer
                                                    items={form.getAllFormData().map((f) => ({ label: f.name }))}
                                                    onChange={(mode: string, newValues: string[]) => {
                                                        rule.watchingValuesMode = mode;
                                                        rule.watchingValues = newValues;
                                                        StateManager.propertyChanged(rule, "watchingValues");
                                                    }}
                                                    value={rule.watchingValues}
                                                    placeholder="Select watching values..."
                                                    label="Run Rules On Value Change"
                                                    valueMode={rule.watchingValuesMode}
                                                />
                                            </WatchValuesRender>
                                        </CellRender>
                                    </TableCell>
                                    <TableCell>
                                        <CellRender
                                            align={"center"}
                                            data-testid="XFormRule-formEnabled"
                                        >
                                            <FormControlLabel
                                                control={
                                                    <UISwitch
                                                        value={rule.enabled}
                                                        onChange={(newValue) => {
                                                            rule.enabled = newValue;
                                                            StateManager.propertyChanged(rule, "enabled");
                                                        }}
                                                    />
                                                }
                                                label=""
                                            />
                                        </CellRender>
                                    </TableCell>
                                    <TableCell>
                                        <CellRender data-testid="XFormRule-expressions">
                                            <ExpressionViewRender>
                                                <ExpressionView
                                                    expression={rule.expression}
                                                    color={{
                                                        result: UIStyleConstants.BLUE_COLOR,
                                                        expression: UIStyleConstants.BLUE_COLOR,
                                                        variable: "" + UIStyleConstants.GRAY_COLOR,
                                                        value: UIStyleConstants.BLACK_COLOR,
                                                        // bracket: {
                                                        //     default: UIStyleConstants.BLUE_COLOR,
                                                        //     error: "#EE3E54",
                                                        //     warning: UIStyleConstants.ORANGE_COLOR,
                                                        // },
                                                    }}
                                                    hideValidation
                                                />

                                                {expressionIssues.length > 0 && (
                                                    <Tooltip title={expressionIssues[0].issue}>
                                                        <div className="error-icon">
                                                            <AttentionIcon />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </ExpressionViewRender>
                                        </CellRender>
                                    </TableCell>
                                    <TableCell>
                                        <CellRender>
                                            <OtherParameters data-testid="XFormRule-otherparameters">
                                                <RuleDataPropertyEditor
                                                    rule={rule}
                                                    form={form}
                                                />

                                                {designIssues.length > 0 && (
                                                    <Tooltip title={designIssues[0].issue}>
                                                        <div className="error-icon">
                                                            <AttentionIcon />
                                                        </div>
                                                    </Tooltip>
                                                )}
                                            </OtherParameters>
                                        </CellRender>
                                    </TableCell>
                                    <TableCell>
                                        <CellRender data-testid="XFormRule-actions">
                                            <IconButton
                                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                    onAction(event.currentTarget);
                                                }}
                                                className="ActionButton"
                                                size="large"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </CellRender>
                                    </TableCell>
                                </StyledTableRow>
                            );
                        }}
                    </Draggable>
                );
            }}
        />
    );
};

const CellRender = styled.div<{
    hideBorder?: boolean;
    align?: "left" | "right" | "center";
    verticalAlign?: "top" | "middle" | "bottom";
}>`
    ${(props) =>
        props.hideBorder ? `border-left: none;` : `border-left: solid 1px  ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};`}

    display: flex;
    padding: 0 16px;
    align-items: ${(props) => props.align || "start"};
    min-height: 82px;

    flex-direction: column;

    position: relative;

    .MuiFormControlLabel-root {
        margin-left: 0;
    }
`;

const StyledTableRow = styled(TableRow)`
    > td {
        border-bottom: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    }

    position: relative;

    .error-icon {
        position: absolute;
        right: 10px;
        bottom: 10px;
    }
`;

const WatchValuesRender = styled.div`
    margin: 0;

    max-width: 216px;
    width: 100%;
`;

const ExpressionViewRender = styled.div`
    background: ${UIStyleConstants.WHITE_SEMI_DARK_COLOR};
    border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    box-sizing: border-box;
    border-radius: 2px;
    width: 100%;
    height: 82px;
    position: relative;
`;

const OtherParameters = styled.div`
    box-sizing: border-box;
    border-radius: 2px;
    min-height: 82px;
    width: 280px;
    position: relative;

    .VariablePicker_Content {
        z-index: 1;
        background: ${UIStyleConstants.WHITE_COLOR};
    }
`;

export const RuleDataPropertyEditor = ({ rule, form }: { rule: FormRule; form: FormConfiguration }) => {
    switch (rule.ruleType) {
        case FormRuleType.VALIDATION:
            return (
                <>
                    <VariablePicker
                        selected={Reflect.get(rule.data!, "controlId")}
                        data={form.getFormDatDefinition()}
                        onVariableSet={function (path: string): void {
                            Reflect.set(rule.data!, "controlId", path);
                            StateManager.propertyChanged(rule, "data");
                        }}
                        label="Attach Validation To"
                        allowNewVariable={false}
                        variant={"standard"}
                        fullWidth={true}
                    />
                    <WrapperWithLabel>
                        <label className="label">Validation Message</label>
                        <InlineTextEdit
                            text={Reflect.get(rule.data!, "validationMessage")}
                            fullWidth
                            onEdit={(newText) => {
                                Reflect.set(rule.data!, "validationMessage", newText);
                                StateManager.propertyChanged(rule, "data");
                            }}
                            placeholder="Validation Message"
                        />
                    </WrapperWithLabel>
                </>
            );
        case FormRuleType.CONTROL_ENABLED:
        case FormRuleType.CONTROL_VISIBILITY:
            let selectedControls: string | string[] = Reflect.get(rule.data!, "controlId");

            if (typeof selectedControls === "string") {
                if (selectedControls !== "") selectedControls = [selectedControls];
                else {
                    selectedControls = [];
                }
            }
            return (
                <WrapperWithLabel>
                    <label className="label">Control Id</label>
                    <CheckboxListLayer
                        items={form
                            .getAllControls()
                            .map((control) => ({ label: control.toString() || "", value: control.id }))}
                        onChange={(mode: string, newValue: string[]) => {
                            Reflect.set(rule, "watchingValuesMode", mode);
                            Reflect.set(rule.data!, "controlId", newValue);
                            StateManager.propertyChanged(rule, "data");
                        }}
                        valueMode={rule.watchingValuesMode}
                        placeholder="Select controls..."
                        label="Select controls"
                        value={selectedControls}
                        renderValue={(selected) => {
                            return (selected as string[])
                                .map((controlId) => {
                                    return form.find(controlId)?.toString();
                                })
                                .filter((v) => !!v)
                                .join(", ");
                        }}
                    />
                </WrapperWithLabel>
            );
        case FormRuleType.CHANGE_FOCUS:
            return (
                <FormControl fullWidth>
                    <InputLabel shrink>Control Id</InputLabel>
                    <StyledSelect
                        value={Reflect.get(rule.data!, "controlId")}
                        fullWidth
                        onChange={(event) => {
                            Reflect.set(rule.data!, "controlId", event.target.value);
                            StateManager.propertyChanged(rule, "data");
                        }}
                    >
                        {form
                            .getAllControls()
                            .filter((control) => control.canFocus)
                            .map((control) => (
                                <MenuItem
                                    key={control.id}
                                    value={control.id}
                                >
                                    {control.toString()}
                                </MenuItem>
                            ))}
                    </StyledSelect>
                </FormControl>
            );
        case FormRuleType.CHANGE_VALUE:
            return (
                <>
                    <VariablePicker
                        selected={Reflect.get(rule.data!, "dataPath")}
                        data={form.getFormDatDefinition()}
                        onVariableSet={(path: string) => {
                            Reflect.set(rule.data!, "dataPath", path);
                            const field = form.getFormDatDefinition().getFieldByDataPath(path);
                            if (field) {
                                const expression = Reflect.get(rule.data!, "value") as ExpressionConfiguration;
                                expression.executionResult = field;
                            }
                            StateManager.propertyChanged(rule, "data");
                        }}
                        label={"data path"}
                        variant={"standard"}
                        size={"small"}
                        fullWidth={true}
                    />

                    <ExpressionViewWithLabel>
                        <label className="label">Value</label>
                        <ExpressionView
                            expression={Reflect.get(rule.data!, "value") as ExpressionConfiguration}
                            onUpdate={() => {
                                StateManager.propertyChanged(rule, "data");
                            }}
                            hideValidation
                            color={{
                                result: UIStyleConstants.BLUE_COLOR,
                                expression: UIStyleConstants.BLUE_COLOR,
                                variable: "" + UIStyleConstants.GRAY_COLOR,
                                value: UIStyleConstants.BLACK_COLOR,
                            }}
                        />
                    </ExpressionViewWithLabel>
                </>
            );
        case FormRuleType.EXIT_FORM:
            return (
                <FormControl fullWidth>
                    <InputLabel shrink>Exit on Path</InputLabel>
                    <StyledSelect
                        value={Reflect.get(rule.data!, "exitOnPath")}
                        fullWidth
                        onChange={(event) => {
                            Reflect.set(rule.data!, "exitOnPath", event.target.value);
                            StateManager.propertyChanged(rule, "data");
                        }}
                    >
                        {form.getOutcomes().map((outcome, idx) => {
                            return (
                                <MenuItem
                                    key={idx}
                                    value={outcome.name}
                                >
                                    {outcome.name}
                                </MenuItem>
                            );
                        })}
                    </StyledSelect>
                </FormControl>
            );
    }
};

const CheckboxListLayer = ({
    items,
    onChange,
    value,
    renderValue,
    placeholder,
    label,
    valueMode,
}: {
    items: { value?: string; label: string }[];
    onChange: (mode: string, newValue: string[]) => void;
    value: string[];
    valueMode: string;
    placeholder?: string;
    renderValue?: (value: string[]) => string | JSX.Element;
    label?: string;
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const [mode, setMode] = useState<"pick" | "value used in rule" | "all">(
        valueMode as "pick" | "value used in rule" | "all",
    );

    return (
        <>
            <ValuesRender
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    setAnchorEl(event.currentTarget);
                }}
                className="CheckboxListLayer-values"
            >
                {value.length ? (renderValue ? renderValue(value) : value.join(", ")) : placeholder}
            </ValuesRender>
            <Menu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => {
                    setAnchorEl(null);
                }}
            >
                <ValuesEditRender>
                    <CloseButton
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            event.stopPropagation();
                            setAnchorEl(null);
                        }}
                    >
                        <CloseGrayIcon />
                    </CloseButton>
                    {label && <ValuesEditLabel>{label}</ValuesEditLabel>}
                    <UIRadioGroup
                        items={[
                            {
                                value: "all",
                            },
                            {
                                value: "value used in rule",
                            },
                            {
                                value: "pick",
                                renderUI: () => {
                                    return (
                                        <CheckListRender>
                                            {items.map((item, idx) => (
                                                <StyledFormControlLabel
                                                    label={item.label}
                                                    key={idx}
                                                    control={
                                                        <StyledCheckBox
                                                            checked={value.includes(item.value || item.label)}
                                                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                                            name={item.value || item.label}
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                if (event.target.checked) {
                                                                    onChange("pick", [
                                                                        ...value,
                                                                        item.value || item.label,
                                                                    ]);
                                                                } else {
                                                                    onChange("pick", [
                                                                        ...value.filter(
                                                                            (v) => v !== (item.value || item.label),
                                                                        ),
                                                                    ]);
                                                                }
                                                            }}
                                                            data-testid={item.value || item.label}
                                                        />
                                                    }
                                                />
                                            ))}
                                        </CheckListRender>
                                    );
                                },
                            },
                        ]}
                        value={mode}
                        onChange={(newMode) => {
                            setMode(newMode as "pick" | "value used in rule" | "all");
                            if (newMode === "all") {
                                onChange(
                                    "all",
                                    items.map((v) => v.value || v.label),
                                );
                            } else if (newMode === "value used in rule") {
                                onChange("value used in rule", []);
                            }
                        }}
                    />
                    <ActionsGroup>
                        <StyledButton
                            sx={{ background: UIStyleConstants.BLUE_COLOR }}
                            variant="contained"
                            size="small"
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                event.stopPropagation();
                                setAnchorEl(null);
                            }}
                        >
                            Done
                        </StyledButton>
                    </ActionsGroup>
                </ValuesEditRender>
            </Menu>
        </>
    );
};

const ValuesRender = styled.div`
    font-family: Source Code Pro;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0em;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
`;

const CheckListRender = styled.div`
    display: flex;
    flex-direction: column;
    padding-left: 15px;

    width: 200px;
    max-height: 150px;
    overflow: auto;
`;

const ValuesEditRender = styled.div`
    padding: 5px 15px;
`;

const ActionsGroup = styled.div`
    display: flex;
    justify-content: flex-end;

    margin-top: 10px;
`;

const CloseButton = styled.div`
    right: 0;
    top: 0;
    position: absolute;

    width: 24px;
    height: 24px;

    opacity: 0.5;

    transition: all 0.1s ease-in;

    &:hover {
        opacity: 1;
    }
`;

const ValuesEditLabel = styled.div`
    font-size: 14px;

    font-weight: 600;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    margin-bottom: 10px;
`;

const WrapperWithLabel = styled.div`
    display: flex;
    flex-direction: column;

    label.label {
        // color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        background: ${UIStyleConstants.WHITE_COLOR};

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        padding: 0;
        font-size: 10px;
        font-family: "Roboto", "Helvetica", "Arial", sans-serif;
        font-weight: 400;
        line-height: 1;
        letter-spacing: 0.00938em;
    }
`;

const ExpressionViewWithLabel = styled.div`
    background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    box-sizing: border-box;
    border-radius: 2px;
    width: 100%;

    margin-top: 25px;

    label {
        position: absolute;
        transform: translateY(-15px);

        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        padding: 0;
        font-size: 10px;
        font-family: "Roboto", "Helvetica", "Arial", sans-serif;
        font-weight: 400;
        line-height: 1;
        letter-spacing: 0.00938em;
    }
`;
